import { isBoundingVolume, isContent, isExtensions, isImpl, } from "./index";
import { Matrix4, Sphere, Vector3 } from "three";
import { Cartographic, Ellipsoid } from "../../../math";
/**
 * This function performs a full traversal of the Tile tree to fully resolve all refine fields. The refine field is
 * optional according to the spec, and when missing should be inherited from the tile's parent. To simplify working with
 * the tiles we will fully resolve all refine fields so other code can just lookup the field without having to hold
 * state.
 * @param tile The tile to resolve refinement modes for
 * @param parent The parent tile to inherit outer refinement mode from
 */
export function propagateRefinementMode(tile, parent) {
    if (parent !== undefined && (tile.refine === undefined || tile.refine === null)) {
        tile.refine = parent.refine;
    }
    // We can only do the propagation if the root tile defines a refinement mode.
    if (typeof tile.refine === "undefined") {
        throw new Error("Tried to propagate refinement mode when the root tile is missing a mode to propagate");
    }
    // Initialize the node stack. The stack pairs the parent's refinement mode with one of the parent's child nodes.
    let nodeStack;
    if (typeof tile.children !== "undefined" && tile.children !== null) {
        nodeStack = tile.children.map((v) => {
            return { refine: tile.refine, tile: v };
        });
    }
    else {
        nodeStack = [];
    }
    // Keep processing nodes until we run out of work to do
    while (nodeStack.length > 0) {
        const current = nodeStack.pop();
        const parentMode = current.refine;
        const node = current.tile;
        // If no refinement mode is provided then we define it to be equal to the parent's refinement mode
        if (typeof node.refine === "undefined" || node.refine === null) {
            node.refine = parentMode;
        }
        // Add the current node's children to the queue paired with the current node's refinement mode for propagating
        if (typeof node.children !== "undefined" && node.children !== null) {
            nodeStack.push(...node.children.map((v) => {
                return { refine: node.refine, tile: v };
            }));
        }
    }
}
/**
 * Walks all nodes in the node tree and ensures that the boundingVolume.sphere field is defined. Will build the sphere
 * from one of the other volume representations if it is not present. This can simplify boundingVolume handling where
 * we can just always use spheres where how coarse our volumes are isn't super important.
 * @param tile
 */
export function resolveBoundingSpheres(tile) {
    // Initialize the node stack. The stack pairs the parent's refinement mode with one of the parent's child nodes.
    const nodeStack = [tile];
    // Keep processing nodes until we run out of work to do
    const scratch = new Sphere();
    while (nodeStack.length > 0) {
        const node = nodeStack.pop();
        // Extract the bounding sphere and write it to the tile
        if (typeof node.boundingVolume.sphere === "undefined" || node.boundingVolume.sphere === null) {
            extractBoundingSphere(node, scratch.makeEmpty());
            node.boundingVolume.sphere = [scratch.center.x, scratch.center.y, scratch.center.z, scratch.radius];
        }
        // Add the current node's children to the queue
        if (typeof node.children !== "undefined" && node.children !== null) {
            nodeStack.push(...node.children);
        }
    }
}
/**
 * Walks the tree from root to leaves, resolving a full local->world transform matrix for each node and storing it into
 * the tile's impl metadata.
 * @param tile
 * @param parent
 */
export function resolveTransforms(tile, parent) {
    // Initialize the world transform metadata for the root tile so we can handle the node stack uniformly
    const rootTransform = new Matrix4();
    loadTransform(tile.transform, rootTransform);
    if (typeof parent !== "undefined") {
        const parentTransform = parent.extras.transform;
        rootTransform.multiply(parentTransform);
    }
    tile.extras.transform.copy(rootTransform);
    // Initialize the node stack. The stack pairs the parent's refinement mode with one of the parent's child nodes.
    const nodeStack = [tile];
    // Keep processing nodes until we run out of work to do
    while (nodeStack.length > 0) {
        const node = nodeStack.pop();
        // Look up the current node's world transform
        const localTransform = node.extras.transform;
        // Apply the parent transform to the child transforms
        if (typeof node.children !== "undefined" && node.children !== null) {
            const SCRATCH = new Matrix4();
            for (const child of node.children) {
                // Create our metadata item and load the local transform from the tile
                const childTransform = new Matrix4();
                loadTransform(child.transform, childTransform);
                // Apply the parent's world transform to produce the fully resolved local->world matrix for the child
                // tile
                SCRATCH.copy(localTransform);
                SCRATCH.multiply(childTransform);
                childTransform.copy(SCRATCH);
                // Add the world transform metadata to the child node
                child.extras.transform.copy(childTransform);
                // Push the child to the stack to process its children
                nodeStack.push(child);
            }
        }
    }
}
/**
 * Simple utility for copying elements from one array to the other. Will copy n elements where n is the length of the
 * smaller of the two arrays.
 * @param src
 * @param dst
 */
function copyArray(src, dst) {
    const len = Math.min(dst.length, src.length);
    for (let i = 0; i < len; i += 1) {
        dst[i] = src[i];
    }
}
function loadTransform(src, dst) {
    if (typeof src !== "undefined" && src !== null) {
        copyArray(src, dst.elements);
    }
}
/**
 * Returns a bounding sphere for the given tile based on the bounding volume it defines. Either copies the existing
 * bounding sphere, or will construct one based on the other bounding volume types
 * @param tile The tile to get a bounding sphere for
 * @param sphere The Sphere instance to write the result into. Defaults to a new Sphere instance
 */
export function extractBoundingSphere(tile, sphere = new Sphere()) {
    const scratch = new Vector3();
    if (typeof tile.boundingVolume.sphere !== "undefined" && tile.boundingVolume.sphere !== null) {
        // First 3 elements is x/y/z of sphere center
        sphere.center.x = tile.boundingVolume.sphere[0];
        sphere.center.y = tile.boundingVolume.sphere[1];
        sphere.center.z = tile.boundingVolume.sphere[2];
        // Last element is the radius of the sphere
        sphere.radius = tile.boundingVolume.sphere[3];
    }
    else if (typeof tile.boundingVolume.box !== "undefined" && tile.boundingVolume.box !== null) {
        // First 3 elements is x/y/z of box center, which becomes the sphere center
        sphere.center.x = tile.boundingVolume.box[0];
        sphere.center.y = tile.boundingVolume.box[1];
        sphere.center.z = tile.boundingVolume.box[2];
        // Clear the radius to make sure it's 0
        sphere.radius = 0;
        // Get the length of all 3 axis
        scratch.x = tile.boundingVolume.box[3];
        scratch.y = tile.boundingVolume.box[4];
        scratch.z = tile.boundingVolume.box[5];
        const x = scratch.length();
        scratch.x = tile.boundingVolume.box[6];
        scratch.y = tile.boundingVolume.box[7];
        scratch.z = tile.boundingVolume.box[8];
        const y = scratch.length();
        sphere.radius = Math.max(sphere.radius, scratch.length());
        scratch.x = tile.boundingVolume.box[9];
        scratch.y = tile.boundingVolume.box[10];
        scratch.z = tile.boundingVolume.box[11];
        const z = scratch.length();
        // Produce a radius that encompasses the whole box
        sphere.radius = Math.sqrt(x * x + y * y + z * z);
    }
    else if (typeof tile.boundingVolume.region !== "undefined" && tile.boundingVolume.region !== null) {
        const cartographic = new Cartographic();
        const region = tile.boundingVolume.region;
        const west = region[0];
        const east = region[2];
        const mLong = (west + east) / 2;
        const south = region[1];
        const north = region[3];
        const mLat = (north + south) / 2;
        const minH = region[4];
        const maxH = region[5];
        const midH = (maxH + minH) / 2;
        // Sample all 8 corners of the region
        //
        // Directly referencing WGS_84 is correct here as the 3D tiles spec states all geodetic regions are defined
        // against WGS 84. Using another ellipsoid definition here makes no sense
        const ellipsoid = Ellipsoid.WGS_84;
        const points = [
            ellipsoid.cartesianForCartographic(cartographic.set(west, south, minH)),
            ellipsoid.cartesianForCartographic(cartographic.set(west, south, maxH)),
            ellipsoid.cartesianForCartographic(cartographic.set(west, south, midH)),
            ellipsoid.cartesianForCartographic(cartographic.set(west, north, minH)),
            ellipsoid.cartesianForCartographic(cartographic.set(west, north, maxH)),
            ellipsoid.cartesianForCartographic(cartographic.set(west, north, midH)),
            ellipsoid.cartesianForCartographic(cartographic.set(west, mLat, minH)),
            ellipsoid.cartesianForCartographic(cartographic.set(west, mLat, maxH)),
            ellipsoid.cartesianForCartographic(cartographic.set(west, mLat, midH)),
            ellipsoid.cartesianForCartographic(cartographic.set(east, south, minH)),
            ellipsoid.cartesianForCartographic(cartographic.set(east, south, maxH)),
            ellipsoid.cartesianForCartographic(cartographic.set(east, south, midH)),
            ellipsoid.cartesianForCartographic(cartographic.set(east, north, minH)),
            ellipsoid.cartesianForCartographic(cartographic.set(east, north, maxH)),
            ellipsoid.cartesianForCartographic(cartographic.set(east, north, midH)),
            ellipsoid.cartesianForCartographic(cartographic.set(east, mLat, minH)),
            ellipsoid.cartesianForCartographic(cartographic.set(east, mLat, maxH)),
            ellipsoid.cartesianForCartographic(cartographic.set(east, mLat, midH)),
            ellipsoid.cartesianForCartographic(cartographic.set(mLong, south, minH)),
            ellipsoid.cartesianForCartographic(cartographic.set(mLong, south, maxH)),
            ellipsoid.cartesianForCartographic(cartographic.set(mLong, south, midH)),
            ellipsoid.cartesianForCartographic(cartographic.set(mLong, north, minH)),
            ellipsoid.cartesianForCartographic(cartographic.set(mLong, north, maxH)),
            ellipsoid.cartesianForCartographic(cartographic.set(mLong, north, midH)),
            ellipsoid.cartesianForCartographic(cartographic.set(mLong, mLat, minH)),
            ellipsoid.cartesianForCartographic(cartographic.set(mLong, mLat, maxH)),
            ellipsoid.cartesianForCartographic(cartographic.set(mLong, mLat, midH)),
        ];
        // Calculate the average (center) of the region
        for (const p of points) {
            sphere.center.add(p.clone().divideScalar(points.length));
        }
        // sphere.center.divideScalar(points.length);
        for (const p of points) {
            sphere.radius = Math.max(sphere.radius, scratch.copy(sphere.center).sub(p).length());
        }
        // A region bounding volume is always defined in terms of world space regardless of the tile's local transform.
        //
        // To make handling these bounding sphere's more uniform we transform the sphere back into local space by
        // applying the inverse of the local->world matrix. That way we can universally apply the local->world matrix to
        // any bounding sphere and get the sphere in world space.
        const inverseLocalToWorld = tile.extras.transform.clone().invert();
        sphere.applyMatrix4(inverseLocalToWorld);
    }
    else {
        throw new Error("Invalid tile boundingVolume, no volume was defined");
    }
    return sphere;
}
/**
 * Reads the bounding box information, if present, into the 4 provided vectors. Returns true if the bounding box data
 * was successfully read or false if no data was present.
 * @param boundingVolume
 * @param position
 * @param x
 * @param y
 * @param z
 */
export function readBoundingBox(boundingVolume, position, x, y, z) {
    if (typeof boundingVolume.box !== "undefined" && boundingVolume.box !== null) {
        position.x = boundingVolume.box[0];
        position.y = boundingVolume.box[1];
        position.z = boundingVolume.box[2];
        x.x = boundingVolume.box[3];
        x.y = boundingVolume.box[4];
        x.z = boundingVolume.box[5];
        y.x = boundingVolume.box[6];
        y.y = boundingVolume.box[7];
        y.z = boundingVolume.box[8];
        z.x = boundingVolume.box[9];
        z.y = boundingVolume.box[10];
        z.z = boundingVolume.box[11];
        return true;
    }
    else {
        return false;
    }
}
/**
 * Reads the bounding sphere information, if present, into the provided object. Returns true if the bounding box data
 * was successfully read or false if no data was present.
 * @param boundingVolume
 * @param sphere
 */
export function readBoundingSphere(boundingVolume, sphere) {
    if (typeof boundingVolume.sphere !== "undefined" && boundingVolume.sphere !== null) {
        sphere.center.x = boundingVolume.sphere[0];
        sphere.center.y = boundingVolume.sphere[1];
        sphere.center.z = boundingVolume.sphere[2];
        sphere.radius = boundingVolume.sphere[3];
        return true;
    }
    else {
        return false;
    }
}
/**
 * Returns whether obj matches the Tile interface
 * @param obj
 */
export function isTile(obj) {
    if (obj === null)
        return false;
    if (typeof obj !== "object")
        return false;
    if (!isBoundingVolume(obj.boundingVolume))
        return false;
    if (typeof obj.viewerRequestVolume !== "undefined" && obj.viewerRequestVolume !== null) {
        if (!isBoundingVolume(obj.boundingVolume))
            return false;
    }
    if (typeof obj.geometricError !== "number")
        return false;
    if (typeof obj.refine !== "undefined" && obj.refine !== null) {
        if (typeof obj.refine !== "string")
            return false;
    }
    if (typeof obj.transform !== "undefined" && obj.transform !== null) {
        if (!Array.isArray(obj.transform))
            return false;
        for (const n of obj.transform) {
            if (typeof n !== "number")
                return false;
        }
    }
    if (typeof obj.content !== "undefined" && obj.content !== null) {
        if (!isContent(obj.content))
            return false;
    }
    if (typeof obj.children !== "undefined" && obj.children !== null) {
        if (!Array.isArray(obj.children))
            return false;
        for (const t of obj.children) {
            if (!isTile(t))
                return false;
        }
    }
    if (typeof obj.extensions !== "undefined" && obj.extensions !== null) {
        if (!isExtensions(obj.extensions))
            return false;
    }
    if (typeof obj.impl !== "undefined") {
        if (!isImpl(obj.impl))
            return false;
    }
    return true;
}
